import { Markets } from '@event/types/event';
import { ICONS } from '@navbar/utils';

enum UserRoles {
    ADMIN = 'Admin',
    EMPLOYEE = 'Employee',
}

interface CompanyOverviewItem {
    icon: keyof typeof ICONS;
    iconClassName: string;
    header: {
        text: string;
        className: string;
    };
    paragraph?: {
        text: string;
        className: string;
    };
    backgroundColor: string;
}

interface ICompanySubmitRequest {
    companyName: string;
    companyType: number | null;
    country: string;
    salesManager: string | null;
}

interface CompanyType {
    id: number;
    name: string;
}

interface Country {
    code: string;
    name: string;
}

interface User {
    id: number;
    full_name: string;
}

enum Services {
    INSTALLER = 'installer',
    RETAIL_SHOP = 'retail_shop',
    DISTRIBUTOR = 'distributor',
    WEBSHOP = 'webshop',
}

interface Service {
    id: number;
    name: Services;
    authorized: boolean;
}

interface ICompany {
    id: string;
    name: string;
    company_type: CompanyType;
    country: Country;
    sales_manager: User;
    services: Service[];
    creation_date: string;
    markets: {
        id: number;
        title: Markets;
    }[];
}

enum StatusRequest {
    Pending = 'Pending',
    Accepted = 'Accepted',
    Rejected = 'Rejected',
}

interface ICompanyRequest {
    id: number;
    status: StatusRequest;
    process_date: string;
    company: Pick<ICompany, 'id' | 'name'>;
    to_company: Pick<ICompany, 'id' | 'name'>;
}

interface IPartnership {
    id: number;
    /**
     * Although this is an array, for now, we want to only assume the first one.
     *
     * This has been future proofed to enable having a partnership with multiple companies involved.
     */
    companies: Omit<ICompany, 'sales_manager' | 'company_type' | 'creation_date'>[];
    /**
     * This can either be empty or only have 1.
     * Although this is an array, for now we want to assume only the first one.
     */
    my_request: ICompanyRequest[];
    /**
     * This can either be empty or only have 1.
     * Although this is an array, for now we want to assume only the first one.
     *
     * This has been future-proofed in case we want multiple companies involved in a single partnership.
     */
    others_requests: ICompanyRequest[];
    active: boolean;
}

interface IPartnershipAnswer {
    id: string;
    request_type: string;
    company: Omit<ICompany, 'sales_manager' | 'company_type' | 'creation_date'>;
    process_date: string;
    process_by: User;
    status: StatusRequest;
}

interface IResultRequestsManagement {
    id: number;
    company: ICompany;
    process_date: string | undefined;
    request_date: string | undefined;
    request_type: string | undefined;
    status: StatusRequest;
    request_by_user: User;
}

interface CompanyType {
    id: number;
    name: string;
}

interface ResultCompaniesManagement {
    id: string;
    name: string;
    company_type: CompanyType;
    country: Country;
    active: boolean;
    sales_manager: User;
    creation_date: string;
}

interface IUserCompaniesManagement {
    count: number;
    next: string | null;
    previous: string | null;
    results: ICompany[];
}

interface IUserRequestsManagement {
    count: number;
    next: string | null;
    previous: string | null;
    results: IResultRequestsManagement[];
}

interface CompaniesHeader {
    id: string;
    name?: string;
    services?: Service[];
    country?: Country;
}

interface CompanyMembersHeader {
    id: string;
    label?: string;
    role_id?: string;
    user__full_name?: string;
}

interface UserRequestsHeader {
    id: number;
    company__name?: string;
    company__company_type?: CompanyType;
    company__country?: Country;
    isrequestProccessed?: string;
    request_type?: string;
    status?: StatusRequest;
    name?: string;
}

enum RequestTypes {
    new_company = 'New Company',
}

type UserCompaniesRequestsTypeColumns =
    | 'id'
    | 'company__name'
    | 'company__company_type'
    | 'company__country'
    | 'request_user__full_name'
    | 'status';

const REQUESTS_VALID_HEADERS = ['company__name', 'company__company_type', 'company__country', 'request_type', 'status'];

enum CompanyAllowedReasonTypes {
    NO_EXACT_ACCOUNT = 'no_exact_account',
}

interface Company {
    id: string;
    name: string;
}

interface Role {
    id: number;
    name: string;
}

interface IEmployee {
    id: string;
    user: User;
    company: Company;
    role: Role;
    is_owner: boolean;
}

interface ICompanyUser {
    count: number;
    next: string | null;
    previous: string | null;
    results: IEmployee[];
}

interface ICompanyMembers {
    id: string;
    idSelected: boolean;
    name: string;
    role: Role;
}

interface CompanyMembersHeader {
    id: string;
    role?: string;
    user__full_name?: string;
}

interface ISubmitRequest {
    inviteeName: string;
    inviteeEmail: string;
}

interface IAddUserSubmitRequest {
    id: string;
    invitee_name: string;
    invitee_email: string;
    company: ICompany;
    created_at: string; // ISO 8601 date string
    status: 'Pending' | 'Accepted' | 'Rejected';
}
type CompanyTypeColumns = 'id' | 'user__full_name' | 'role';

interface ICompanySchema {
    name: string;
    vat_number?: string;
    chamber_of_commerce_number: string;
    services: Omit<Service, 'name'>[];
    markets_ids: number[];
    address: string;
    city?: string;
    country_code: string;
    province?: string;
    phone_number?: string;
    fax_number?: string;
    commercial_email: string;
    support_emails: string;
    website?: string;
    sales_manager_id?: string;
    description?: string;
    owner_id?: number;
    isAdmin: boolean;
}

export { CompanyAllowedReasonTypes, REQUESTS_VALID_HEADERS, RequestTypes, Services, StatusRequest, UserRoles };
export type {
    CompaniesHeader,
    CompanyMembersHeader,
    CompanyOverviewItem,
    CompanyType,
    CompanyTypeColumns,
    IAddUserSubmitRequest,
    ICompany,
    ICompanyMembers,
    ICompanySchema,
    ICompanySubmitRequest,
    ICompanyUser,
    IEmployee,
    IPartnership,
    IPartnershipAnswer,
    IResultRequestsManagement,
    ISubmitRequest,
    IUserCompaniesManagement,
    IUserRequestsManagement,
    ResultCompaniesManagement,
    Role,
    UserCompaniesRequestsTypeColumns,
    UserRequestsHeader,
};
